import React, { useEffect, useState } from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown, X, Save, Edit2 } from 'lucide-react';
import { useApi } from '../auth/useApi';

const C = {
  primary: 'var(--c-primary)',
  secondary: 'var(--c-secondary)',
  bg: 'var(--c-bg)',
  surface: 'var(--c-surface)',
  border: 'var(--c-border)',
  text: 'var(--c-text)',
  textMuted: 'var(--c-textMuted)',
  ok: 'var(--c-ok)',
  warn: 'var(--c-warn)',
  danger: 'var(--c-danger)',
};

// Field types supported by backend
export const FIELD_TYPES = ['text', 'textarea', 'date', 'select', 'multiselect', 'checkbox'] as const;
export type FieldType = typeof FIELD_TYPES[number];

export interface FormField {
  FieldId?: number;
  FormId?: number;
  Label: string;
  FieldType: FieldType;
  Required: boolean;
  OptionsJson: string | null;     // JSON array of option strings
  SortOrder: number;
}

export interface FormDef {
  FormId: number;
  Name: string;
  Icon: string | null;
  Description: string | null;
  IsDefault: boolean;
  AutoAssignUserId: number | null;
  AutoCategoryId: number | null;
  AutoPriority: 'Low' | 'Medium' | 'High' | 'Critical' | null;
  AutoTagsJson: string | null;
  Visibility?: 'public' | 'private';
}

interface FormsResponse {
  forms: FormDef[];
  fields: FormField[];
}

interface User { UserId: number; DisplayName: string; Role: string; }
interface Category { CategoryId: number; Name: string; }

// -------- Helpers --------
const optionsToList = (json: string | null): string[] => {
  if (!json) return [];
  try { const v = JSON.parse(json); return Array.isArray(v) ? v : []; }
  catch { return []; }
};

// -------- Reusable buttons --------
const Btn: React.FC<{
  children: React.ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'ghost' | 'danger';
  size?: 'sm' | 'md';
  disabled?: boolean;
  style?: React.CSSProperties;
}> = ({ children, onClick, variant = 'primary', size = 'md', disabled, style }) => {
  const palette = {
    primary: { bg: C.primary, fg: '#fff', border: C.primary },
    ghost: { bg: 'transparent', fg: C.text, border: C.border },
    danger: { bg: C.danger, fg: '#fff', border: C.danger },
  }[variant];
  return (
    <button onClick={onClick} disabled={disabled} style={{
      background: disabled ? '#ccc' : palette.bg, color: palette.fg, border: `1px solid ${palette.border}`,
      padding: size === 'sm' ? '4px 10px' : '8px 14px', borderRadius: 6,
      fontSize: size === 'sm' ? 12 : 13, fontWeight: 500, cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1, display: 'inline-flex', alignItems: 'center', gap: 6, ...style,
    }}>{children}</button>
  );
};

// ============================================================================
// Forms Tab (export)
// ============================================================================
export const FormsTab: React.FC<{ isAdmin: boolean }> = ({ isAdmin }) => {
  const api = useApi();
  const [forms, setForms] = useState<FormDef[]>([]);
  const [fields, setFields] = useState<FormField[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<FormDef | null>(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [formsData, userList, catList] = await Promise.all([
        api.get<FormsResponse>('/api/admin/forms'),
        api.get<User[]>('/api/users'),
        api.get<Category[]>('/api/categories').catch(() => [] as Category[]),
      ]);
      setForms(formsData.forms ?? []);
      setFields(formsData.fields ?? []);
      setUsers(Array.isArray(userList) ? userList.filter(u => u.Role === 'admin' || u.Role === 'tech') : []);
      setCategories(Array.isArray(catList) ? catList : []);
      setError(null);
    } catch (e: any) { setError(e?.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const fieldsFor = (formId: number) =>
    fields.filter(f => f.FormId === formId).sort((a, b) => a.SortOrder - b.SortOrder);

  const handleDelete = async (form: FormDef) => {
    if (form.IsDefault) { alert('Cannot delete the default form.'); return; }
    if (!window.confirm(`Delete "${form.Name}"? This cannot be undone.`)) return;
    try {
      await api.del(`/api/admin/forms/${form.FormId}`);
      await load();
    } catch (e: any) { alert(`Failed: ${e?.message}`); }
  };

  if (loading) return <div style={{ padding: 20, color: C.textMuted }}>Loading forms…</div>;
  if (error) return <div style={{ padding: 20, color: C.danger }}>Error: {error}</div>;

  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <p style={{ fontSize: 13, color: C.textMuted, margin: 0 }}>
          Forms shape the "New Ticket" experience. The default form cannot be deleted.
        </p>
        <Btn onClick={() => setCreating(true)}><Plus size={12} /> New Form</Btn>
      </div>

      {forms.map(f => {
        const ff = fieldsFor(f.FormId);
        return (
          <div key={f.FormId} style={{
            padding: 14, borderBottom: `1px solid ${C.border}`,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
              <div style={{ fontSize: 24 }}>{f.Icon ?? '📝'}</div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>
                  {f.Name}
                  {f.IsDefault && (
                    <span style={{
                      marginLeft: 8, fontSize: 10, padding: '2px 8px',
                      background: `${C.secondary}20`, color: C.secondary,
                      borderRadius: 3, fontWeight: 600,
                      textTransform: 'uppercase', letterSpacing: '0.4px',
                    }}>Default</span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>
                  {ff.length} {ff.length === 1 ? 'field' : 'fields'}
                  {f.Description ? ` · ${f.Description}` : ''}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <Btn variant="ghost" size="sm" onClick={() => setEditing(f)}>
                <Edit2 size={11} /> Edit
              </Btn>
              {isAdmin && !f.IsDefault && (
                <Btn variant="ghost" size="sm" onClick={() => handleDelete(f)}>
                  <Trash2 size={11} />
                </Btn>
              )}
            </div>
          </div>
        );
      })}

      {forms.length === 0 && (
        <div style={{ padding: 24, color: C.textMuted, textAlign: 'center', fontSize: 13 }}>
          No forms yet.
        </div>
      )}

      {(editing || creating) && (
        <FormEditor
          form={editing}
          existingFields={editing ? fieldsFor(editing.FormId) : []}
          users={users}
          categories={categories}
          onSave={async (formBody) => {
            try {
              if (editing) {
                await api.patch(`/api/admin/forms/${editing.FormId}`, formBody);
              } else {
                await api.post('/api/admin/forms', formBody);
              }
              setEditing(null); setCreating(false);
              await load();
            } catch (e: any) { alert(`Failed: ${e?.message}`); }
          }}
          onClose={() => { setEditing(null); setCreating(false); }}
        />
      )}
    </div>
  );
};

// ============================================================================
// Form Editor Modal
// ============================================================================
const FormEditor: React.FC<{
  form: FormDef | null;
  existingFields: FormField[];
  users: User[];
  categories: Category[];
  onSave: (body: any) => Promise<void>;
  onClose: () => void;
}> = ({ form, existingFields, users, categories, onSave, onClose }) => {
  const [name, setName] = useState(form?.Name ?? '');
  const [icon, setIcon] = useState(form?.Icon ?? '📝');
  const [description, setDescription] = useState(form?.Description ?? '');
  const [autoAssign, setAutoAssign] = useState<number | null>(form?.AutoAssignUserId ?? null);
  const [autoCategory, setAutoCategory] = useState<number | null>(form?.AutoCategoryId ?? null);
  const [autoPriority, setAutoPriority] = useState<string>(form?.AutoPriority ?? '');
  const [visibility, setVisibility] = useState<'public' | 'private'>(form?.Visibility ?? 'public');
  const [fields, setFields] = useState<FormField[]>(
    existingFields.length > 0
      ? existingFields.map(f => ({ ...f }))   // clone
      // Start with NO custom fields. Ticket creation has a built-in "Description"
      // textarea already; adding another would be a duplicate. Admin adds any
      // extra fields they want via the "Add field" button below.
      : []
  );
  const [saving, setSaving] = useState(false);

  const update = (idx: number, patch: Partial<FormField>) => {
    setFields(prev => prev.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  };
  const remove = (idx: number) => setFields(prev => prev.filter((_, i) => i !== idx));
  const move = (idx: number, dir: -1 | 1) => {
    const target = idx + dir;
    if (target < 0 || target >= fields.length) return;
    const next = [...fields];
    [next[idx], next[target]] = [next[target], next[idx]];
    setFields(next);
  };
  const addField = () => setFields(prev => [...prev, {
    Label: 'New Field', FieldType: 'text', Required: false, OptionsJson: null,
    SortOrder: prev.length + 1,
  }]);

  const save = async () => {
    if (!name.trim()) { alert('Form name is required.'); return; }
    if (fields.length === 0) { alert('Add at least one field.'); return; }
    for (const f of fields) {
      if (!f.Label.trim()) { alert('Every field needs a label.'); return; }
      if ((f.FieldType === 'select' || f.FieldType === 'multiselect') && optionsToList(f.OptionsJson).length === 0) {
        alert(`Field "${f.Label}" is a ${f.FieldType} — it needs options.`); return;
      }
    }
    setSaving(true);
    try {
      const body: any = {
        name: name.trim(),
        icon: icon || null,
        description: description.trim() || null,
        autoAssignUserId: autoAssign,
        autoCategoryId: autoCategory,
        autoPriority: autoPriority || null,
        visibility,
        fields: fields.map((f, i) => ({
          label: f.Label.trim(),
          fieldType: f.FieldType,
          required: !!f.Required,
          options: f.FieldType === 'select' || f.FieldType === 'multiselect'
            ? optionsToList(f.OptionsJson)
            : undefined,
          sortOrder: i + 1,
        })),
      };
      await onSave(body);
    } finally { setSaving(false); }
  };

  return (
    <div style={modalBackdrop}>
      <div style={{ ...modalCard, maxWidth: 820 }}>
        <div style={modalHeader}>
          <h3 style={{ margin: 0, color: C.primary, fontSize: 16 }}>
            {form ? `Edit Form: ${form.Name}` : 'New Form'}
          </h3>
          <button onClick={onClose} style={modalCloseBtn}><X size={16} /></button>
        </div>

        <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
          {/* Basics */}
          <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: 12, marginBottom: 12 }}>
            <Field label="Icon">
              <input value={icon ?? ''} onChange={e => setIcon(e.target.value.slice(0, 4))}
                style={{ ...inputSty(), textAlign: 'center', fontSize: 20 }} maxLength={4} />
            </Field>
            <Field label="Form Name">
              <input value={name} onChange={e => setName(e.target.value)}
                style={inputSty()} placeholder="e.g. New Hire Setup" autoFocus />
            </Field>
          </div>

          <Field label="Visibility">
            <select value={visibility} onChange={e => setVisibility(e.target.value as 'public' | 'private')} style={inputSty()}>
              <option value="public">Public - anyone can pick this form</option>
              <option value="private">Private - hidden from end users (techs and admins only)</option>
            </select>
          </Field>

          <Field label="Description">
            <input value={description ?? ''} onChange={e => setDescription(e.target.value)}
              style={inputSty()} placeholder="Optional — shown under the form name in the picker" />
          </Field>

          {/* Auto-actions */}
          <div style={{
            padding: 14, background: C.bg, borderRadius: 6, marginBottom: 16,
            border: `1px solid ${C.border}`,
          }}>
            <div style={{
              fontSize: 11, color: C.textMuted, fontWeight: 600,
              textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 10,
            }}>
              Auto-actions on submit
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
              <Field label="Auto-assign to">
                <select value={autoAssign ?? ''} onChange={e => setAutoAssign(e.target.value ? parseInt(e.target.value) : null)} style={inputSty()}>
                  <option value="">— none —</option>
                  {users.map(u => <option key={u.UserId} value={u.UserId}>{u.DisplayName}</option>)}
                </select>
              </Field>
              <Field label="Auto-category">
                <select value={autoCategory ?? ''} onChange={e => setAutoCategory(e.target.value ? parseInt(e.target.value) : null)} style={inputSty()}>
                  <option value="">— none —</option>
                  {categories.map(c => <option key={c.CategoryId} value={c.CategoryId}>{c.Name}</option>)}
                </select>
              </Field>
              <Field label="Auto-priority">
                <select value={autoPriority} onChange={e => setAutoPriority(e.target.value)} style={inputSty()}>
                  <option value="">— let user choose —</option>
                  {['Critical', 'High', 'Medium', 'Low'].map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </Field>
            </div>
          </div>

          {/* Field editor */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{
              fontSize: 11, color: C.textMuted, fontWeight: 600,
              textTransform: 'uppercase', letterSpacing: '0.4px',
            }}>Fields</div>
            <Btn variant="ghost" size="sm" onClick={addField}><Plus size={12} /> Add Field</Btn>
          </div>

          {fields.length === 0 && (
            <div style={{
              padding: 24, textAlign: 'center', color: C.textMuted, fontSize: 13,
              border: `1px dashed ${C.border}`, borderRadius: 6,
            }}>No fields yet. Click "+ Add Field" to start.</div>
          )}

          {fields.map((f, idx) => (
            <div key={idx} style={{
              padding: 12, marginBottom: 8, border: `1px solid ${C.border}`,
              borderRadius: 6, background: C.bg,
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr auto', gap: 8, alignItems: 'end' }}>
                <Field compact label="Label">
                  <input value={f.Label} onChange={e => update(idx, { Label: e.target.value })} style={inputSty()} />
                </Field>
                <Field compact label="Type">
                  <select value={f.FieldType}
                    onChange={e => {
                      const newType = e.target.value as FieldType;
                      update(idx, {
                        FieldType: newType,
                        OptionsJson: (newType === 'select' || newType === 'multiselect') && !f.OptionsJson
                          ? JSON.stringify(['Option 1'])
                          : f.OptionsJson,
                      });
                    }} style={inputSty()}>
                    {FIELD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
                <div style={{ display: 'flex', gap: 4 }}>
                  <Btn variant="ghost" size="sm" onClick={() => move(idx, -1)} disabled={idx === 0}><ChevronUp size={12} /></Btn>
                  <Btn variant="ghost" size="sm" onClick={() => move(idx, 1)} disabled={idx === fields.length - 1}><ChevronDown size={12} /></Btn>
                  <Btn variant="ghost" size="sm" onClick={() => remove(idx)}><Trash2 size={12} /></Btn>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
                <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input type="checkbox" checked={f.Required}
                    onChange={e => update(idx, { Required: e.target.checked })} />
                  Required
                </label>
                {(f.FieldType === 'select' || f.FieldType === 'multiselect') && (
                  <div style={{ flex: 1 }}>
                    <input
                      value={optionsToList(f.OptionsJson).join(', ')}
                      onChange={e => {
                        const opts = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
                        update(idx, { OptionsJson: opts.length > 0 ? JSON.stringify(opts) : null });
                      }}
                      placeholder="Comma-separated options: Option 1, Option 2"
                      style={{ ...inputSty(), fontSize: 12 }}
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div style={modalFooter}>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn onClick={save} disabled={saving || !name.trim()}>
            <Save size={12} /> {saving ? 'Saving…' : 'Save Form'}
          </Btn>
        </div>
      </div>
    </div>
  );
};

// -------- Shared styles --------
const modalBackdrop: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 100, padding: 20,
};
const modalCard: React.CSSProperties = {
  background: C.surface, borderRadius: 8, width: '100%',
  maxHeight: '90vh', display: 'flex', flexDirection: 'column',
};
const modalHeader: React.CSSProperties = {
  padding: '14px 20px', borderBottom: `1px solid ${C.border}`,
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
};
const modalFooter: React.CSSProperties = {
  padding: '12px 20px', borderTop: `1px solid ${C.border}`,
  display: 'flex', justifyContent: 'flex-end', gap: 8,
};
const modalCloseBtn: React.CSSProperties = {
  background: 'none', border: 'none', cursor: 'pointer',
  padding: 4, color: C.textMuted, display: 'inline-flex',
};
const inputSty = (): React.CSSProperties => ({
  border: `1px solid ${C.border}`, padding: '7px 10px', borderRadius: 6,
  fontSize: 13, width: '100%', boxSizing: 'border-box', background: C.surface,
});
const Field: React.FC<{ label: string; compact?: boolean; children: React.ReactNode }> = ({ label, compact, children }) => (
  <div style={{ marginBottom: compact ? 0 : 12 }}>
    <div style={{
      fontSize: compact ? 10 : 11, fontWeight: 600, color: C.textMuted,
      marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.4px',
    }}>{label}</div>
    {children}
  </div>
);
